import React, { useEffect, useMemo, useRef, useState } from "react";
import {
    createColumnHelper,
    useReactTable,
    getCoreRowModel,
    getFilteredRowModel,
    getSortedRowModel,
    flexRender,
} from "@tanstack/react-table";
import { IoChevronDown, IoChevronForward } from "react-icons/io5";
import { useDealData } from "contexts/DealContext";
import { usePortfolioData } from "contexts/PortfolioContext";
import { useViewMode } from "contexts/ViewModeContext";
import { exportFinanceTableToCSV } from "utils/csvExport";
import {
    occupancyOrder,
    revenueOrder,
    expenseOrder,
    ebitdarOrder,
    adjustmentsOrder,
    sortByMetricOrder,
} from "utils/metricOrder";
import {
    TimePeriodSelector,
    TableContainer,
    TableHeader,
    formatCellValue,
    isLastInPeriodGroup,
    filterEbitdaItems,
    CalculatedProjectionsBadge,
    isProjection1Period,
    hasProjection1Period,
} from "./FinanceTables";

const columnHelper = createColumnHelper();

// Expense category mappings for expandable subcategories
const categoryMap = {
    'Housekeeping Laundry': {
        mainType: 'Housekeeping Laundry',
        subTypes: ['Housekeeping - Labor', 'Housekeeping - Non Labor']
    },
    'Food and Nutrition': {
        mainType: 'Food and Nutrition',
        subTypes: ['Food and Nutrition - Labor', 'Food and Nutrition - Non Labor']
    },
    'Maintenance': {
        mainType: 'Maintenance',
        subTypes: ['Maintenance - Labor', 'Maintenance - Non Labor']
    },
    'Recreation': {
        mainType: 'Recreation',
        subTypes: ['Recreation - Labor', 'Recreation - Non Labor']
    },
    'Payroll Taxes Benefits': {
        mainType: 'Payroll Taxes Benefits',
        subTypes: ['Payroll Taxes', 'Workers Comp', 'Benefits']
    }
};

const findCategory = (type) => {
    return Object.entries(categoryMap).find(([_, config]) =>
        config.mainType === type || config.subTypes.includes(type)
    );
};

// Portfolio backend collapses everything under `category == "Ancillary"` into a
// single "Ancillary Expenses" row with detailed account-titles as SubMetrics.
// Deal-side historically breaks these out into Therapy / Pharmacy Expense /
// Other Ancillaries at the top level. Split by SubMetric title prefix so the
// two views match.
const ANCILLARY_SPLIT_RULES = [
    { name: 'Therapy', match: (t) => /Therapy/i.test(t) },
    { name: 'Pharmacy Expense', match: (t) => /^Pharmacy Exp/i.test(t) },
    { name: 'Other Ancillaries', match: (t) => /^Other Ancillary/i.test(t) },
];

const splitAncillary = (row) => {
    const subs = Array.isArray(row.SubMetrics) ? row.SubMetrics : [];
    if (subs.length === 0) return [row];

    const buckets = new Map();
    const unclassified = [];
    for (const sub of subs) {
        const rule = ANCILLARY_SPLIT_RULES.find((r) => r.match(sub.Type || ''));
        if (rule) {
            if (!buckets.has(rule.name)) buckets.set(rule.name, []);
            buckets.get(rule.name).push(sub);
        } else {
            unclassified.push(sub);
        }
    }
    if (buckets.size === 0) return [row];

    const sum = (arr, k) => arr.reduce((s, x) => s + (Number(x[k]) || 0), 0);
    const out = [];
    for (const rule of ANCILLARY_SPLIT_RULES) {
        const bucket = buckets.get(rule.name);
        if (!bucket || bucket.length === 0) continue;
        out.push({
            Type: rule.name,
            Annual: sum(bucket, 'Annual'),
            PPD: sum(bucket, 'PPD'),
            Percentages: '',
            SubMetrics: bucket,
        });
    }
    if (unclassified.length > 0) {
        out.push({
            Type: 'Ancillary Expenses',
            Annual: sum(unclassified, 'Annual'),
            PPD: sum(unclassified, 'PPD'),
            Percentages: '',
            SubMetrics: unclassified,
        });
    }
    return out;
};

// Walk an expense list, expanding any "Ancillary Expenses" row into the split
// buckets above. No-op on deal-side data (which has no "Ancillary Expenses" row).
const augmentExpenseList = (list) => {
    if (!Array.isArray(list)) return list;
    const out = [];
    for (const item of list) {
        if (item?.Type === 'Ancillary Expenses') {
            out.push(...splitAncillary(item));
        } else {
            out.push(item);
        }
    }
    return out;
};

const isAdjustmentType = (type) => {
    if (!type) return false;
    return adjustmentsOrder.some(adj => adj.toLowerCase() === type.toLowerCase());
};


const processExpenseData = (data) => {
    const filteredData = filterEbitdaItems(data);
    const processedData = [];
    const processedTypes = new Set();
    const categoryStorage = {};

    Object.entries(categoryMap).forEach(([category]) => {
        categoryStorage[category] = { subcategories: [], mainItem: null };
    });

    // First pass: collect subcategories
    filteredData.forEach(item => {
        const categoryEntry = findCategory(item.Type);
        if (categoryEntry) {
            const [categoryName, config] = categoryEntry;
            if (config.subTypes.includes(item.Type)) {
                categoryStorage[categoryName].subcategories.push(item);
            } else if (item.Type === config.mainType) {
                categoryStorage[categoryName].mainItem = item;
            }
        }
    });

    // Second pass: process in order
    filteredData.forEach(item => {
        if (processedTypes.has(item.Type)) return;
        const categoryEntry = findCategory(item.Type);

        if (categoryEntry) {
            const [categoryName, config] = categoryEntry;
            if (item.Type === config.mainType) {
                const storage = categoryStorage[categoryName];
                if (storage.subcategories.length > 0) {
                    const totals = storage.subcategories.reduce((acc, sub) => ({
                        Annual: acc.Annual + (Number(sub.Annual) || 0),
                        PPD: acc.PPD + (Number(sub.PPD) || 0),
                    }), { Annual: 0, PPD: 0 });

                    // Special handling for Payroll Taxes Benefits percentages
                    if (categoryName === 'Payroll Taxes Benefits') {
                        const totalPct = storage.subcategories.reduce((sum, sub) => {
                            if (sub.Percentages == null) return sum;
                            const num = typeof sub.Percentages === 'number'
                                ? sub.Percentages
                                : parseFloat(String(sub.Percentages).replace('%', ''));
                            return sum + (isNaN(num) ? 0 : num);
                        }, 0);
                        totals.Percentages = `${totalPct.toFixed(1)}%`;
                    }

                    processedData.push({
                        ...storage.mainItem,
                        Type: categoryName,
                        Annual: totals.Annual,
                        PPD: totals.PPD,
                        Percentages: totals.Percentages,
                        isMainCategory: true,
                        subcategories: storage.subcategories,
                    });
                } else {
                    // No sibling detail rows for this category (portfolio-side
                    // data keeps the breakdown inside `item.SubMetrics` instead
                    // of as separate top-level rows). Push the parent row as-is
                    // so its S3 SubMetrics still drive the expander later.
                    processedData.push(item);
                }
            }
            processedTypes.add(item.Type);
        } else {
            processedData.push(item);
            processedTypes.add(item.Type);
        }
    });

    return sortByMetricOrder(processedData, expenseOrder);
};

export default function FinancialModelTable({ availableTimePeriods = [], currentPeriod }) {
    const { viewMode } = useViewMode();
    const {
        currentFacility: dealFacility,
        currentDealName,
    } = useDealData();
    const {
        currentPortfolioFacility,
        currentPortfolioFacilityName,
    } = usePortfolioData();

    const currentFacility = viewMode === 'portfolio' ? currentPortfolioFacility : dealFacility;

    const [tableData, setTableData] = useState([]);
    const [expandedRows, setExpandedRows] = useState({});

    // Default period selection differs by view:
    //   - portfolio: only trailing periods (T12/T6/T3/T1); months are opt-in via
    //     the period selector.
    //   - deals: all available periods.
    const defaultSelectedPeriods = useMemo(() => {
        if (viewMode === 'portfolio') {
            const trailing = availableTimePeriods.filter(p => /^T(12|6|3|1)\b/.test(p));
            if (trailing.length > 0) return trailing;
        }
        return availableTimePeriods.length > 0 ? availableTimePeriods : [currentPeriod];
    }, [availableTimePeriods, currentPeriod, viewMode]);

    const [selectedPeriods, setSelectedPeriods] = useState(defaultSelectedPeriods);
    const hasInitializedRef = useRef(false);

    useEffect(() => {
        if (availableTimePeriods.length === 0) return;

        // First time real periods arrive: seed the selection with the default.
        if (!hasInitializedRef.current) {
            hasInitializedRef.current = true;
            setSelectedPeriods(defaultSelectedPeriods);
            return;
        }

        // Subsequent facility/portfolio switches: preserve the user's selection
        // (including any manually-added months) — only drop periods that no
        // longer exist in the new facility. If pruning wipes everything, fall
        // back to the default.
        const pruned = selectedPeriods.filter(p => availableTimePeriods.includes(p));
        if (pruned.length !== selectedPeriods.length) {
            setSelectedPeriods(pruned.length > 0 ? pruned : defaultSelectedPeriods);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [availableTimePeriods, defaultSelectedPeriods]);

    // Build combined table data from all sections
    useEffect(() => {
        if (!currentFacility?.periods) return;

        // Row skeleton is the UNION of Types across every selected period — not
        // just the first one. Otherwise a metric that only has values in
        // projection columns (blank in trailing periods) would never get a row
        // and its projection values would have nowhere to render.
        const periodsToScan = selectedPeriods.length > 0 ? selectedPeriods : [currentPeriod];
        const collectAcrossPeriods = (categoryKey, transform = (x) => x) => {
            const merged = new Map();
            periodsToScan.forEach(p => {
                const raw = currentFacility?.periods?.[p]?.Categories?.[categoryKey] || [];
                const items = transform(raw);
                items.forEach(item => {
                    if (!item || !item.Type) return;
                    const existing = merged.get(item.Type);
                    if (!existing) {
                        merged.set(item.Type, item);
                    } else if (
                        (!Array.isArray(existing.SubMetrics) || existing.SubMetrics.length === 0) &&
                        Array.isArray(item.SubMetrics) && item.SubMetrics.length > 0
                    ) {
                        merged.set(item.Type, item);
                    }
                });
            });
            return [...merged.values()];
        };

        const rows = [];

        // -- OCCUPANCY section --
        rows.push({ Type: 'OCCUPANCY', _isSectionHeader: true, _section: 'Occupancy' });
        const occupancyItems = collectAcrossPeriods('Occupancy');
        const sortedOccupancy = sortByMetricOrder(occupancyItems, occupancyOrder);
        sortedOccupancy.forEach(item => {
            rows.push({ ...item, _section: 'Occupancy' });
        });

        // -- REVENUE section --
        rows.push({ Type: 'REVENUE', _isSectionHeader: true, _section: 'Revenue' });
        const revenueItems = collectAcrossPeriods('Revenue', (raw) =>
            filterEbitdaItems(raw).filter(item => !isAdjustmentType(item.Type))
        );
        const sortedRevenue = sortByMetricOrder(revenueItems, revenueOrder);
        sortedRevenue.forEach(item => {
            if (item && item.Type) {
                const hasSubMetrics =
                    Array.isArray(item.SubMetrics) && item.SubMetrics.length > 0;
                rows.push({
                    ...item,
                    _section: 'Revenue',
                    ...(hasSubMetrics
                        ? { isMainCategory: true, subcategories: item.SubMetrics }
                        : {}),
                });
            }
        });

        // -- EXPENSES section --
        rows.push({ Type: 'EXPENSES', _isSectionHeader: true, _section: 'Expenses' });
        const expenseUnion = collectAcrossPeriods('Expenses', (raw) => augmentExpenseList(raw));
        const processedExpenses = processExpenseData(expenseUnion)
            .filter(item => !isAdjustmentType(item.Type));
        processedExpenses.forEach(item => {
            if (item && item.Type) {
                // Attach S3 SubMetrics as expandable children only if the row
                // wasn't already made expandable by the hardcoded categoryMap.
                const hasSubMetrics =
                    !item.isMainCategory &&
                    Array.isArray(item.SubMetrics) &&
                    item.SubMetrics.length > 0;
                rows.push({
                    ...item,
                    _section: 'Expenses',
                    ...(hasSubMetrics
                        ? { isMainCategory: true, subcategories: item.SubMetrics }
                        : {}),
                });
            }
        });

        // -- EBITDAR rows (appended to Expenses, no separate section header) --
        const ebitdarItems = collectAcrossPeriods('Expenses', (raw) =>
            raw.filter(item =>
                item && item.Type && (
                    item.Type.toUpperCase().includes('EBITDA') ||
                    item.Type.toUpperCase().includes('EBITDAR')
                ) && !item.Type.includes('Check')
                && !isAdjustmentType(item.Type)
            )
        );
        const sortedEbitdar = sortByMetricOrder(ebitdarItems, ebitdarOrder);
        sortedEbitdar.forEach(item => {
            rows.push({ ...item, _section: 'EBITDAR' });
        });

        // -- ADJUSTMENTS section --
        rows.push({ Type: 'ADJUSTMENTS', _isSectionHeader: true, _section: 'Adjustments' });
        const adjustmentItemsMap = new Map();
        periodsToScan.forEach(p => {
            const rev = currentFacility?.periods?.[p]?.Categories?.Revenue || [];
            const exp = currentFacility?.periods?.[p]?.Categories?.Expenses || [];
            [...rev, ...exp].forEach(item => {
                if (item && item.Type && isAdjustmentType(item.Type) && !adjustmentItemsMap.has(item.Type)) {
                    adjustmentItemsMap.set(item.Type, { ...item, _section: 'Adjustments' });
                }
            });
        });
        const sortedAdjustments = sortByMetricOrder([...adjustmentItemsMap.values()], adjustmentsOrder);
        sortedAdjustments.forEach(item => {
            rows.push(item);
        });

        setTableData(rows);
    }, [currentFacility, currentPeriod, selectedPeriods]);

    const toggleRowExpansion = (rowType) => {
        setExpandedRows(prev => ({ ...prev, [rowType]: !prev[rowType] }));
    };

    // Prepare rows including expanded subcategories
    const preparedRows = useMemo(() => {
        const result = [];
        tableData.forEach(row => {
            result.push(row);
            if (row.isMainCategory && expandedRows[row.Type] && row.subcategories) {
                result.push(...row.subcategories.map(sub => ({
                    ...sub,
                    isSubcategory: true,
                    parentCategory: row.Type,
                    _section: row._section,
                })));
            }
        });
        return result;
    }, [tableData, expandedRows]);

    // Format occupancy values (no $ prefix)
    const formatOccupancyValue = (value, isADC = false) => {
        if (!value && value !== 0) return '-';
        if (isNaN(value)) return value;
        return isADC
            ? Number(value).toLocaleString(undefined, { minimumFractionDigits: 1, maximumFractionDigits: 1 })
            : Math.round(Number(value)).toLocaleString();
    };

    // Columns
    const columns = useMemo(() => {
        const typeColumn = columnHelper.accessor("Type", {
            id: "type",
            header: () => <p className="text-sm font-bold text-gray-600 dark:text-white">TYPE</p>,
            cell: (info) => {
                const row = info.row.original;
                const value = info.getValue();

                if (row._isSectionHeader) {
                    return null; // section headers rendered as colSpan rows
                }

                if (!value) return <p className="text-sm font-bold text-gray-900">-</p>;

                if (row.isMainCategory) {
                    return (
                        <div
                            className="flex items-center cursor-pointer"
                            onClick={() => toggleRowExpansion(row.Type)}
                        >
                            {row.subcategories?.length > 0 && (
                                expandedRows[row.Type]
                                    ? <IoChevronDown size={16} />
                                    : <IoChevronForward size={16} />
                            )}
                            <p className="text-sm font-bold text-gray-900 ml-2">{value}</p>
                        </div>
                    );
                }

                if (row.isSubcategory) {
                    return (
                        <div className="flex items-center">
                            <div className="w-6 border-r border-gray-200 mr-2"></div>
                            <p className="text-sm text-gray-900 flex-1">{value}</p>
                        </div>
                    );
                }

                return <p className="text-sm font-bold text-gray-900">{value}</p>;
            },
        });

        const orderedPeriods = [...selectedPeriods].sort((a, b) => {
            const indexA = availableTimePeriods.indexOf(a);
            const indexB = availableTimePeriods.indexOf(b);
            return indexA - indexB;
        });

        const periodColumnGroups = orderedPeriods.map(period => {
            return columnHelper.group({
                id: `${period}-group`,
                header: period ? period.toUpperCase() : '',
                isPeriodGroup: true,
                columns: [
                    // Annual column
                    columnHelper.accessor(row => {
                        if (row._isSectionHeader) return { value: null, section: row._section };
                        const section = row._section;
                        let match;
                        if (section === 'Adjustments') {
                            const revData = currentFacility?.periods?.[period]?.Categories?.Revenue || [];
                            const expData = currentFacility?.periods?.[period]?.Categories?.Expenses || [];
                            match = [...revData, ...expData].find(item => item.Type === row.Type);
                        } else {
                            const categoryKey = section === 'EBITDAR' ? 'Expenses' : section;
                            const rawPeriodData = currentFacility?.periods?.[period]?.Categories?.[categoryKey] || [];
                            const periodData = categoryKey === 'Expenses'
                                ? augmentExpenseList(rawPeriodData)
                                : rawPeriodData;
                            match = periodData.find(item => item.Type === row.Type);
                            if (!match && row.isSubcategory && row.parentCategory) {
                                const parent = periodData.find(item => item.Type === row.parentCategory);
                                if (parent && Array.isArray(parent.SubMetrics)) {
                                    match = parent.SubMetrics.find(sub => sub.Type === row.Type);
                                }
                            }
                        }
                        return {
                            value: match?.Annual,
                            section,
                            isPlaceholder: match?._placeholder,
                            tooltip: match?._tooltip?.annual,
                        };
                    }, {
                        id: `${period}-annual`,
                        header: () => <p className="text-sm font-bold text-right text-gray-600 dark:text-white">ANNUAL</p>,
                        cell: (info) => {
                            const data = info.getValue() || {};
                            if (info.row.original._isSectionHeader) return null;
                            const { value, section, isPlaceholder, tooltip } = data;
                            const displayTitle = tooltip || (isPlaceholder ? 'Placeholder - calculation not finalized' : undefined);

                            if (section === 'Occupancy') {
                                return (
                                    <p className="text-right text-sm text-gray-900">
                                        {formatOccupancyValue(value)}
                                    </p>
                                );
                            }

                            return (
                                <p
                                    className={`text-right text-sm ${isPlaceholder ? 'text-orange-500 italic' : 'text-gray-900'} ${displayTitle ? 'cursor-help' : ''}`}
                                    title={displayTitle}
                                >
                                    {formatCellValue(value)}{isPlaceholder ? '*' : ''}
                                </p>
                            );
                        },
                    }),
                    // PPD / ADC / Percentages column
                    columnHelper.accessor(row => {
                        if (row._isSectionHeader) return { value: null, section: row._section };
                        const section = row._section;
                        let match;
                        if (section === 'Adjustments') {
                            const revData = currentFacility?.periods?.[period]?.Categories?.Revenue || [];
                            const expData = currentFacility?.periods?.[period]?.Categories?.Expenses || [];
                            match = [...revData, ...expData].find(item => item.Type === row.Type);
                        } else {
                            const categoryKey = section === 'EBITDAR' ? 'Expenses' : section;
                            const rawPeriodData = currentFacility?.periods?.[period]?.Categories?.[categoryKey] || [];
                            const periodData = categoryKey === 'Expenses'
                                ? augmentExpenseList(rawPeriodData)
                                : rawPeriodData;
                            match = periodData.find(item => item.Type === row.Type);
                            if (!match && row.isSubcategory && row.parentCategory) {
                                const parent = periodData.find(item => item.Type === row.parentCategory);
                                if (parent && Array.isArray(parent.SubMetrics)) {
                                    match = parent.SubMetrics.find(sub => sub.Type === row.Type);
                                }
                            }
                        }

                        if (section === 'Occupancy') {
                            return {
                                value: match?.Percentages || match?.ADC || match?.PPD,
                                section,
                                isPercentage: !!match?.Percentages,
                            };
                        }
                        if (section === 'EBITDAR') {
                            // Show margin (PPD field is the margin ratio)
                            return { value: match?.PPD, section };
                        }
                        if (section === 'Expenses' || section === 'Adjustments') {
                            const isEbitdarRow = row.Type && (
                                row.Type.toUpperCase().includes('EBITDA') ||
                                row.Type.toUpperCase().includes('EBITDAR')
                            );
                            if (isEbitdarRow) {
                                return { value: match?.PPD, section, isMargin: true };
                            }
                            return {
                                value: match?.Percentages || match?.PPD,
                                isPPD: !match?.Percentages && match?.PPD,
                                section,
                            };
                        }
                        // Revenue
                        return {
                            value: match?.PPD,
                            section,
                            isPlaceholder: match?._placeholder,
                            tooltip: match?._tooltip?.ppd,
                        };
                    }, {
                        id: `${period}-ppd`,
                        header: () => <p className="text-sm font-bold text-right text-gray-600 dark:text-white">PPD</p>,
                        cell: (info) => {
                            const data = info.getValue() || {};
                            if (info.row.original._isSectionHeader) return null;
                            const { value, section, isPlaceholder, tooltip, isPPD, isPercentage, isMargin } = data;

                            if (!value && value !== 0) return <p className="text-right text-sm text-gray-900">-</p>;

                            // EBITDAR-type rows anywhere — show as margin %
                            if (isMargin) {
                                const numValue = typeof value === 'number' ? value : parseFloat(value);
                                if (isNaN(numValue)) return <p className="text-right text-sm text-gray-900">-</p>;
                                return <p className="text-right text-sm text-gray-900">{(numValue * 100).toFixed(1)}%</p>;
                            }

                            // Occupancy section
                            if (section === 'Occupancy') {
                                if (isPercentage) {
                                    const strVal = typeof value === 'number' ? value.toString() : value;
                                    const numVal = parseFloat(String(strVal).replace('%', ''));
                                    return (
                                        <p className="text-right text-sm text-gray-900">
                                            {!isNaN(numVal) ? `${numVal.toFixed(1)}%` : value}
                                        </p>
                                    );
                                }
                                return (
                                    <p className="text-right text-sm text-gray-900">
                                        {formatOccupancyValue(value, true)}
                                    </p>
                                );
                            }

                            // EBITDAR section — show margin as percentage
                            if (section === 'EBITDAR') {
                                const numValue = typeof value === 'number' ? value : parseFloat(value);
                                if (isNaN(numValue)) return <p className="text-right text-sm text-gray-900">-</p>;
                                return (
                                    <p className="text-right text-sm text-gray-900">
                                        {(numValue * 100).toFixed(1)}%
                                    </p>
                                );
                            }

                            // Expenses section
                            if (section === 'Expenses') {
                                if (typeof value === 'string' && isNaN(parseFloat(value))) {
                                    return <p className={`text-right text-sm ${isPPD ? 'text-red-900' : 'text-gray-900'}`}>{value}</p>;
                                }
                                const strValue = typeof value === 'number' ? value.toString() : value;
                                const numValue = typeof strValue === 'string' ? parseFloat(strValue.replace('%', '')) : parseFloat(strValue);
                                if (isPPD) {
                                    return <p className="text-right text-sm text-red-900">{formatCellValue(numValue, true)}</p>;
                                }
                                return (
                                    <p className="text-right text-sm text-gray-900">
                                        {!isNaN(numValue) ? `${numValue.toFixed(1)}%` : value}
                                    </p>
                                );
                            }

                            // Revenue section
                            const displayTitle = tooltip || (isPlaceholder ? 'Placeholder - calculation not finalized' : undefined);
                            return (
                                <p
                                    className={`text-right text-sm ${isPlaceholder ? 'text-orange-500 italic' : 'text-red-900'} ${displayTitle ? 'cursor-help' : ''}`}
                                    title={displayTitle}
                                >
                                    {formatCellValue(value, true)}{isPlaceholder ? '*' : ''}
                                </p>
                            );
                        },
                    }),
                ],
            });
        });

        return [typeColumn, ...periodColumnGroups];
    }, [selectedPeriods, availableTimePeriods, currentFacility, expandedRows]);

    const table = useReactTable({
        data: preparedRows,
        columns,
        getCoreRowModel: getCoreRowModel(),
        getFilteredRowModel: getFilteredRowModel(),
        getSortedRowModel: getSortedRowModel(),
    });

    const handleTogglePeriod = (periods) => {
        setSelectedPeriods(prev => {
            if (periods.length > 1) return periods;
            const period = periods[0];
            if (prev.includes(period)) {
                if (period === currentPeriod && prev.length === 1) return prev;
                return prev.filter(p => p !== period);
            }
            return [...prev, period];
        });
    };

    const handleResetPeriods = () => {
        setSelectedPeriods(defaultSelectedPeriods);
    };

    const handleExport = () => {
        const name = viewMode === 'portfolio'
            ? (currentPortfolioFacilityName || 'portfolio')
            : (currentDealName || 'deal');
        const facilityName = currentFacility?.details?.Facilities || 'facility';
        const periodsText = selectedPeriods.length > 1 ? 'multiple_periods' : selectedPeriods[0];
        const filename = `${name}_${facilityName}_financial_model_${periodsText}.csv`;

        exportFinanceTableToCSV(
            preparedRows.filter(r => !r._isSectionHeader),
            columns,
            currentFacility,
            selectedPeriods,
            filename
        );
    };

    const showCalculatedBadge = false;

    const timePeriodSelector = (
        <TimePeriodSelector
            availablePeriods={availableTimePeriods}
            selectedPeriods={selectedPeriods}
            onTogglePeriod={handleTogglePeriod}
            onResetPeriods={handleResetPeriods}
        />
    );

    // Count total columns for colSpan on section headers
    const totalColumns = table.getHeaderGroups()[0]?.headers?.reduce(
        (sum, h) => sum + (h.colSpan || 1), 0
    ) || 1;

    return (
        <TableContainer
            title="Financial Model"
            timePeriodSelector={timePeriodSelector}
            onExport={handleExport}
            showCalculatedBadge={showCalculatedBadge}
        >
            <TableHeader headerGroups={table.getHeaderGroups()} dark={false} />
            <tbody>
                {table.getRowModel().rows.map((row, index) => {
                    const original = row.original;

                    // Section header row
                    if (original._isSectionHeader) {
                        return (
                            <tr key={row.id}>
                                <td
                                    className="px-2 py-1.5 text-xs font-bold text-white uppercase tracking-widest border-b border-slate-500 min-w-[200px]"
                                    style={{
                                        position: 'sticky',
                                        left: 0,
                                        zIndex: 20,
                                        backgroundColor: '#475569',
                                        boxShadow: '2px 0 4px rgba(0,0,0,0.08)',
                                    }}
                                >
                                    {original.Type}
                                </td>
                                {totalColumns > 1 && (
                                    <td
                                        colSpan={totalColumns - 1}
                                        className="border-b border-slate-500"
                                        style={{ backgroundColor: '#475569' }}
                                    />
                                )}
                            </tr>
                        );
                    }

                    const isEbitdar = original._section === 'EBITDAR' || original.Type?.toUpperCase().includes('EBITDAR');
                    const isTotal = original.Type?.toLowerCase().includes('total');
                    const isAdjusted = original.Type?.toLowerCase().includes('adjusted');

                    // Slate isn't in the project's Tailwind palette, so bg-slate-*
                    // classes wouldn't emit CSS — apply the projection-builder's
                    // slate hexes inline so the sticky Type column can inherit them.
                    const rowStyle = isEbitdar
                        ? { backgroundColor: '#cbd5e1' } // slate-300
                        : (isAdjusted || isTotal)
                            ? { backgroundColor: '#e2e8f0' } // slate-200
                            : original.isSubcategory
                                ? { backgroundColor: '#F5F6FA' } // gray-50 from theme
                                : { backgroundColor: '#ffffff' };

                    const rowClass = isEbitdar
                        ? 'font-bold border-t-2 border-t-gray-400'
                        : (isAdjusted || isTotal)
                            ? 'font-bold border-t-2 border-t-gray-300'
                            : original.isSubcategory
                                ? 'hover:bg-gray-100'
                                : '';

                    return (
                        <tr
                            key={row.id}
                            className={rowClass}
                            style={rowStyle}
                        >
                            {row.getVisibleCells().map((cell, cellIndex) => {
                                const isLastColumn = cellIndex === row.getVisibleCells().length - 1;
                                const isTypeColumn = cell.column.id === 'type';
                                const isLastInGroup = isLastInPeriodGroup(cell, row, isLastColumn);
                                const borderClass = isLastInGroup ? 'border-r-2 border-gray-400' : '';

                                const periodKey = cell.column.id.replace(/-annual$|-ppd$/, '');
                                const isProjectionCell = !isTypeColumn && /projection/i.test(periodKey);
                                const projectionBg = isProjectionCell && !isEbitdar
                                    ? isTotal
                                        ? { backgroundColor: '#bfdbfe' }   // blue-200
                                        : isAdjusted
                                            ? { backgroundColor: '#dbeafe' } // blue-100
                                            : { backgroundColor: '#eff6ff' } // blue-50
                                    : {};

                                const frozenColumnStyles = isTypeColumn ? {
                                    position: 'sticky',
                                    left: 0,
                                    zIndex: 20,
                                    backgroundColor: 'inherit',
                                    boxShadow: '2px 0 4px rgba(0,0,0,0.08)'
                                } : {};

                                return (
                                    <td
                                        key={cell.id}
                                        className={`px-2 py-1 border border-gray-200 ${isTypeColumn
                                            ? 'text-left min-w-[200px]'
                                            : 'text-right min-w-[120px]'
                                            } ${borderClass}`}
                                        style={{ ...frozenColumnStyles, ...projectionBg }}
                                    >
                                        {flexRender(
                                            cell.column.columnDef.cell,
                                            cell.getContext()
                                        )}
                                    </td>
                                );
                            })}
                        </tr>
                    );
                })}
            </tbody>
        </TableContainer>
    );
}
